'use client'

import { useState, type FormEvent } from 'react'
import GrandTitle from '@/components/GrandTitle'
import type { NewsletterContent } from '@/components/NewsletterModal'
import { NEWSLETTER_SUBSCRIBE_URL } from '@/lib/newsletter'
import { palette, type Fond } from '@/lib/palette'

/**
 * Bloc « Rester informé » — inscription newsletter INLINE (champ email + bouton),
 * fin de page communauté générale. Remplace les 3 cartes (Newsletter/WhatsApp/
 * Instagram) : Instagram & WhatsApp vivent désormais comme icônes du footer.
 * Même adresse d'inscription que la modale (lib/newsletter.ts).
 *
 * ÉDITABLE DEPUIS LE CRM (2026-10-15) : le texte vient de la section
 * « Newsletter » (`content`), avec repli sur les valeurs par défaut ci-dessous
 * — un champ laissé vide ne change donc rien à l'écran. Avant, ce bloc était
 * codé en dur et la section Newsletter n'alimentait rien : l'éditer était sans
 * effet. C'est le bloc que le plan de l'éditeur ouvre quand on clique « Rester
 * informé » (sourceId = 'newsletter').
 */
const SUBMIT_URL = NEWSLETTER_SUBSCRIBE_URL

/** Repli = les textes historiques, pour ne rien changer tant que rien n'est saisi. */
const DEFAUTS = {
  eyebrow: 'Rester informé',
  title: 'Restez dans la lumière',
  subtitle: 'Recevez en avant-première les invitations, les temps forts et les ouvertures de billetterie du Gala des Étoiles.',
  submitLabel: "M'inscrire",
  placeholder: 'vous@email.com',
  successMessage: 'Merci — votre inscription est bien prise en compte. À très vite dans la lumière du Gala.',
  errorMessage: 'Une erreur est survenue. Merci de réessayer dans un instant.',
}

export default function ResterInforme({ fond = 'light', content }: { fond?: Fond; content?: NewsletterContent }) {
  const p = palette(fond)
  const [email, setEmail] = useState('')
  const [website, setWebsite] = useState('') // honeypot
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle')

  /* Un champ non renseigné retombe sur le texte historique. */
  const txt = (v: string | undefined, repli: string) => (typeof v === 'string' && v.trim() ? v.trim() : repli)
  const eyebrow = txt(content?.eyebrow, DEFAUTS.eyebrow)
  const title = txt(content?.title, DEFAUTS.title)
  const subtitle = txt(content?.subtitle, DEFAUTS.subtitle)
  const submitLabel = txt(content?.submitLabel, DEFAUTS.submitLabel)
  const placeholder = txt(content?.placeholder, DEFAUTS.placeholder)
  const successMessage = txt(content?.successMessage, DEFAUTS.successMessage)
  const errorMessage = txt(content?.errorMessage, DEFAUTS.errorMessage)

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (status === 'submitting') return
    if (website) return // honeypot
    setStatus('submitting')
    try {
      const res = await fetch(SUBMIT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, source: 'gala-des-etoiles' }),
      })
      if (!res.ok) throw new Error()
      setStatus('success')
    } catch {
      setStatus('error')
    }
  }

  return (
    <section id="rester-informe" style={{ backgroundColor: p.fond, padding: 'clamp(72px, 9vw, 120px) 24px', textAlign: 'center', fontFamily: 'var(--font-body, inherit)' }}>
      <div className="mx-auto" style={{ maxWidth: 640 }}>
        <div style={{ fontSize: 'clamp(11px, 0.6875rem + 0.15vw, 13px)', letterSpacing: '0.34em', textTransform: 'uppercase', fontWeight: 600, color: p.accent }}>{eyebrow}</div>
        <div style={{ width: 44, height: 1, background: p.accent, margin: '16px auto 24px', opacity: 0.9 }} aria-hidden />
        <GrandTitle dark={p.sombre}>{title}</GrandTitle>

        {status === 'success' ? (
          <p style={{ margin: '26px auto 0', maxWidth: 520, fontSize: 16, lineHeight: 1.7, color: p.titre }}>
            {successMessage}
          </p>
        ) : (
          <>
            <p style={{ margin: '20px auto 0', maxWidth: 520, fontSize: 15, lineHeight: 1.7, color: p.texte(0.72) }}>
              {subtitle}
            </p>
            <form onSubmit={onSubmit} className="ri-form" style={{ margin: '32px auto 0', maxWidth: 560, display: 'flex', gap: 12 }}>
              <input
                type="text" value={website} onChange={(e) => setWebsite(e.target.value)}
                tabIndex={-1} autoComplete="off" aria-hidden="true"
                style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, opacity: 0 }}
              />
              <input
                type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder={placeholder} aria-label="Votre adresse email"
                style={{ flex: 1, minWidth: 0, background: '#fff', border: `1px solid ${p.texte(0.22)}`, borderRadius: 2, padding: '14px 16px', fontFamily: 'var(--font-body, inherit)', fontSize: 15, color: '#021734' }}
              />
              <button type="submit" className="gde-btn gde-btn--solid" disabled={status === 'submitting'} style={{ whiteSpace: 'nowrap' }}>
                {status === 'submitting' ? 'Envoi…' : submitLabel}
              </button>
            </form>
            {status === 'error' && (
              <p style={{ margin: '12px auto 0', fontSize: 13, color: '#a3402f' }}>{errorMessage}</p>
            )}
            <p style={{ margin: '16px auto 0', fontSize: 12, color: p.texte(0.5) }}>
              Pas de spam — désinscription en un clic. En vous inscrivant, vous acceptez notre{' '}
              <a href="/confidentialite" style={{ color: 'inherit', textDecoration: 'underline' }}>politique de confidentialité</a>.
            </p>
          </>
        )}
      </div>
      <style>{`@media (max-width: 560px){ .ri-form{ flex-direction: column !important; } }`}</style>
    </section>
  )
}
